import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { es as esDateLocale } from "date-fns/locale";
import { esES as esMuiLocale } from "@mui/material/locale";
import { esES as esPickersLocale } from "@mui/x-date-pickers/locales";

import { NAMESPACES, RESOURCES } from "./resources";
import swDateLocale from "./swDateLocale";
import { swMaterial, swPickers } from "./swMuiLocale";

const STORAGE_KEY = "healthdesk.language";

/**
 * The languages offered in Settings, each named in itself so a patient who cannot read
 * the current one can still find theirs. Strings with no translation yet fall back to English.
 */
export const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "sw", label: "Kiswahili" },
];

const SUPPORTED = LANGUAGES.map((l) => l.code);

const readStoredLanguage = () => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (SUPPORTED.includes(stored)) return stored;
  } catch (e) {
    // Storage blocked (private window, etc.): treated as System.
  }
  return null;
};

/**
 * System: the first of the browser's languages we have ("sw-KE" → "sw"), else English.
 * What a visitor gets before choosing, so a Swahili phone opens in Swahili, signed in or not.
 */
export const detectLanguage = () => {
  const preferred =
    (typeof navigator !== "undefined" && (navigator.languages || [navigator.language])) || [];
  for (const tag of preferred) {
    const base = String(tag || "").toLowerCase().split(/[-_]/)[0];
    if (SUPPORTED.includes(base)) return base;
  }
  return "en";
};

/** "auto" until a language is picked, then its code; what the settings menus show as chosen. */
export const languagePreference = () => readStoredLanguage() || "auto";

i18n.use(initReactI18next).init({
  resources: RESOURCES,
  ns: NAMESPACES,
  defaultNS: "translation",
  lng: readStoredLanguage() || detectLanguage(),
  fallbackLng: "en",
  supportedLngs: SUPPORTED,
  // React already escapes what it renders.
  interpolation: { escapeValue: false },
  // Resources are bundled, so there is nothing to wait for: render synchronously.
  initImmediate: false,
  react: { useSuspense: false },
});

// No document under the node-environment test suites (src/tests/cloud, src/tests/backend),
// which load this through setupTests.
const syncDocumentLanguage = (code) => {
  if (typeof document !== "undefined") document.documentElement.lang = code;
};
syncDocumentLanguage(i18n.language);
i18n.on("languageChanged", syncDocumentLanguage);

/**
 * Switch the app's language and remember it on this device, like the theme. "auto" forgets
 * the choice and follows the browser again.
 */
export const setLanguage = (code) => {
  const auto = code === "auto" || !SUPPORTED.includes(code);
  i18n.changeLanguage(auto ? detectLanguage() : code);
  try {
    if (auto) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, code);
  } catch (e) {
    // Not persisted; the choice still applies for this session.
  }
};

const DATE_LOCALES = { es: esDateLocale, sw: swDateLocale };

/** date-fns `locale` option for the current language; undefined means English. */
export const dateLocale = () => DATE_LOCALES[i18n.language];

const MUI_LOCALES = { es: [esMuiLocale, esPickersLocale], sw: [swMaterial, swPickers] };

/**
 * MUI's own strings (pagination, autocomplete, ...) and the date pickers' (Cancel, OK, toolbar
 * titles) for a language, as theme fragments to merge with createTheme. Empty for English.
 */
export const muiLocales = (code) => MUI_LOCALES[code] || [];

export default i18n;
